import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { TenantThemeConfig } from '../types/theme'
import {
  getCashbackDisplayPromotionConfig,
  type UserCashbackDisplaySettings,
} from '../services/promotionConfigService'
import {
  claimCashbackSettlement,
  getCashbackHistory,
  getCashbackMe,
  type MemberCashbackSettlement,
  type MemberCashbackSummary,
} from '../services/cashbackService'
import * as authService from '../services/authService'
import './ModernCashbackPage.css'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

interface ModernCashbackPageProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

/** TEMP: HTD theme preview — set true ชั่วคราวเมื่อต้องการดู UI ทั้งที่ระบบปิด */
const HTD_CASHBACK_UI_PREVIEW = false

const PREVIEW_CLAIMABLE: MemberCashbackSettlement = {
  _id: 'preview-claimable',
  periodKey: '2026-07-15',
  periodStartAt: '2026-07-15T00:00:00.000Z',
  periodEndAt: '2026-07-15T23:59:59.000Z',
  status: 'claimable',
  calculation: { cashbackRate: 0.5, cashbackAmount: 128.5 },
  sourceSummary: { netLoss: 25700, validTurnover: 180000 },
  bonusLock: { turnoverMultiplier: 1, turnoverRequired: 128.5, withdrawPolicy: 'after_turnover' },
}

const PREVIEW_HISTORY: MemberCashbackSettlement[] = [
  PREVIEW_CLAIMABLE,
  {
    _id: 'preview-paid-1',
    periodKey: '2026-07-14',
    periodStartAt: '2026-07-14T00:00:00.000Z',
    periodEndAt: '2026-07-14T23:59:59.000Z',
    status: 'paid',
    calculation: { cashbackRate: 0.5, cashbackAmount: 86.2 },
    sourceSummary: { netLoss: 17240, validTurnover: 95000 },
    bonusLock: { turnoverMultiplier: 1 },
    payout: { paidAt: '2026-07-15T08:00:00.000Z' },
  },
  {
    _id: 'preview-pending-1',
    periodKey: '2026-07-13',
    periodStartAt: '2026-07-13T00:00:00.000Z',
    periodEndAt: '2026-07-13T23:59:59.000Z',
    status: 'pending',
    calculation: { cashbackRate: 0.4, cashbackAmount: 42 },
    sourceSummary: { netLoss: 10500, validTurnover: 62000 },
    bonusLock: { turnoverMultiplier: 1 },
    claim: { claimedAt: '2026-07-14T10:00:00.000Z' },
  },
]

const PREVIEW_SUMMARY: MemberCashbackSummary = {
  claimableAmount: 128.5,
  pendingAmount: 42,
  approvedAmount: 0,
  paidAmount: 86.2,
  totalEarned: 256.7,
}

const defaultDisplay: UserCashbackDisplaySettings = {
  pageTitle: 'เงินคืนยอดเล่น',
  pageSubtitle: 'ตรวจสอบยอดคืนล่าสุด กดรับ cashback ได้ทันที และดูเงื่อนไขได้ในหน้าจอเดียว',
  heroBadgeText: 'Cashback Center',
  withdrawalButtonText: 'ถอนเงินแนะนำ',
  noticeText: 'รายได้การคืนยอดเล่นของท่านสามารถแจ้งถอนได้ทุกเวลา หากมียอดรายได้มากกว่า 15 บาทขึ้นไป',
  minWithdrawalAmount: 15,
  cashbackRateText: '0.3% - 0.8%',
  promotionalMessage: 'สุดยอดโปรโมชั่น รับเงินคืนฟรี ทันทีทุกวัน ทั้งวัน ไม่อั้น ไม่ติดเทิร์น ง่ายๆ',
  disabledTitle: 'ปิดใช้งานชั่วคราว',
  disabledMessage: 'ระบบ cashback ปิดใช้งานในขณะนี้ กรุณาติดต่อแอดมินหากมีคำถาม',
  historyEmptyText: 'ยังไม่มีประวัติ cashback',
  showRateTable: true,
  showExample: true,
  promotionRoute: '/cashback',
  promotionCard: {
    imageUrl: '',
    imageAlt: 'คืนยอดเสีย',
    badgeText: 'Cashback',
    title: 'คืนยอดเสีย',
    description: 'ดูยอด cashback ล่าสุด กดรับสิทธิ์ และดูประวัติการรับเงินคืน',
    buttonText: 'ไปหน้า Cashback',
  },
}

const ModernCashbackPage: React.FC<ModernCashbackPageProps> = ({ themeConfig }) => {
  const [displayConfig, setDisplayConfig] = useState<UserCashbackDisplaySettings>(defaultDisplay)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [cashbackDisabled, setCashbackDisabled] = useState(false)
  const [uiPreviewMode, setUiPreviewMode] = useState(false)
  const [cashbackSummary, setCashbackSummary] = useState<MemberCashbackSummary>({
    claimableAmount: 0,
    pendingAmount: 0,
    approvedAmount: 0,
    paidAmount: 0,
    totalEarned: 0,
  })
  const [latestClaimable, setLatestClaimable] = useState<MemberCashbackSettlement | null>(null)
  const [cashbackHistory, setCashbackHistory] = useState<MemberCashbackSettlement[]>([])
  const [claiming, setClaiming] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [claimMessage, setClaimMessage] = useState('')
  const refreshCashbackConfigRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const refreshCashbackDataRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const applyPreviewData = useCallback(() => {
    setUiPreviewMode(true)
    setCashbackDisabled(false)
    setCashbackSummary(PREVIEW_SUMMARY)
    setLatestClaimable(PREVIEW_CLAIMABLE)
    setCashbackHistory(PREVIEW_HISTORY)
    setError(null)
  }, [])

  const loadCashbackDisplayConfig = useCallback(async () => {
    try {
      const configRes = await getCashbackDisplayPromotionConfig(themeConfig.tenantId)
      const nextSettings = configRes?.data?.settings
      if (configRes?.success && nextSettings) {
        setDisplayConfig((prev) => ({
          ...prev,
          ...nextSettings,
        }))
      } else {
        setDisplayConfig(defaultDisplay)
      }
    } catch {
      setDisplayConfig(defaultDisplay)
    }
  }, [themeConfig.tenantId])

  const loadCashbackData = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) {
      setLoading(true)
    }
    setError(null)

    try {
      const [meRes, historyRes] = await Promise.all([
        getCashbackMe(),
        getCashbackHistory(10),
      ])

      setCashbackDisabled(false)
      setUiPreviewMode(false)
      if (meRes?.success) {
        setCashbackSummary(meRes.data.summary)
        setLatestClaimable(meRes.data.latestClaimable)
      }
      if (historyRes?.success) {
        setCashbackHistory(Array.isArray(historyRes.data) ? historyRes.data : [])
      }
    } catch (err) {
      const errorLike = err as { message?: string; status?: number }
      const isDisabled =
        errorLike?.status === 404 ||
        String(errorLike?.message || '').toLowerCase().includes('not enabled')

      if (isDisabled) {
        if (HTD_CASHBACK_UI_PREVIEW) {
          applyPreviewData()
        } else {
          setUiPreviewMode(false)
          setCashbackDisabled(true)
          setCashbackSummary({
            claimableAmount: 0,
            pendingAmount: 0,
            approvedAmount: 0,
            paidAmount: 0,
            totalEarned: 0,
          })
          setLatestClaimable(null)
          setCashbackHistory([])
        }
      } else {
        setError(err instanceof Error ? err.message : 'ไม่สามารถโหลดข้อมูล cashback ล่าสุดได้')
      }
    } finally {
      if (!options?.silent) {
        setLoading(false)
      }
      setRefreshing(false)
    }
  }, [applyPreviewData])

  useEffect(() => {
    let active = true

    const loadData = async () => {
      try {
        await loadCashbackDisplayConfig()
        if (!active) return
        await loadCashbackData()
      } finally {
        if (!active) return
      }
    }

    void loadData()

    return () => {
      active = false
    }
  }, [loadCashbackData, loadCashbackDisplayConfig])

  useEffect(() => {
    const handleCashbackConfigUpdate = (event: Event) => {
      const detail = (event as CustomEvent)?.detail as {
        contentType?: string
        type?: string
        promotionType?: string
        username?: string
        userId?: string
        content?: {
          promotionType?: string
          type?: string
          username?: string
          userId?: string
        }
      } | undefined

      const isCashbackSystemUpdate =
        detail?.type === 'cashback_config_updated' ||
        detail?.promotionType === 'cashback' ||
        detail?.content?.type === 'cashback_config_updated' ||
        detail?.content?.promotionType === 'cashback' ||
        (detail?.contentType === 'promotion_config' && (
          detail?.promotionType === 'cashback' ||
          detail?.content?.promotionType === 'cashback'
        ))

      const isCashbackDisplayUpdate =
        detail?.type === 'cashback_display_config_updated' ||
        detail?.promotionType === 'cashback_display' ||
        detail?.content?.type === 'cashback_display_config_updated' ||
        detail?.content?.promotionType === 'cashback_display' ||
        (detail?.contentType === 'promotion_config' && (
          detail?.promotionType === 'cashback_display' ||
          detail?.content?.promotionType === 'cashback_display'
        ))

      const isCashbackSettlementUpdate =
        detail?.type === 'cashback_settlement_updated' ||
        detail?.content?.type === 'cashback_settlement_updated'

      const currentUser = authService.getCurrentUser()
      const currentUsername = String(currentUser?.username || '').trim().toLowerCase()
      const currentUserId = String(currentUser?.id || '').trim()
      const targetUsername = String(detail?.username || detail?.content?.username || '').trim().toLowerCase()
      const targetUserId = String(detail?.userId || detail?.content?.userId || '').trim()
      const isForCurrentMember =
        (!targetUsername && !targetUserId) ||
        (!!targetUsername && targetUsername === currentUsername) ||
        (!!targetUserId && !!currentUserId && targetUserId === currentUserId)

      if (!isCashbackSystemUpdate && !isCashbackDisplayUpdate && !isCashbackSettlementUpdate) return
      if (isCashbackSettlementUpdate && !isForCurrentMember) return

      if (isCashbackSystemUpdate || isCashbackDisplayUpdate) {
        if (refreshCashbackConfigRef.current) {
          clearTimeout(refreshCashbackConfigRef.current)
        }
        refreshCashbackConfigRef.current = setTimeout(() => {
          void loadCashbackDisplayConfig().catch(() => {})
        }, 250)
      }

      if (isCashbackSystemUpdate || isCashbackSettlementUpdate) {
        if (refreshCashbackDataRef.current) {
          clearTimeout(refreshCashbackDataRef.current)
        }
        refreshCashbackDataRef.current = setTimeout(() => {
          void loadCashbackData({ silent: true }).catch(() => {})
        }, 250)
      }
    }

    window.addEventListener('content_update', handleCashbackConfigUpdate as EventListener)
    window.addEventListener('cashback_config_updated', handleCashbackConfigUpdate as EventListener)
    window.addEventListener('cashback_display_config_updated', handleCashbackConfigUpdate as EventListener)
    window.addEventListener('cashback_settlement_updated', handleCashbackConfigUpdate as EventListener)

    return () => {
      if (refreshCashbackConfigRef.current) {
        clearTimeout(refreshCashbackConfigRef.current)
        refreshCashbackConfigRef.current = null
      }
      if (refreshCashbackDataRef.current) {
        clearTimeout(refreshCashbackDataRef.current)
        refreshCashbackDataRef.current = null
      }
      window.removeEventListener('content_update', handleCashbackConfigUpdate as EventListener)
      window.removeEventListener('cashback_config_updated', handleCashbackConfigUpdate as EventListener)
      window.removeEventListener('cashback_display_config_updated', handleCashbackConfigUpdate as EventListener)
      window.removeEventListener('cashback_settlement_updated', handleCashbackConfigUpdate as EventListener)
    }
  }, [loadCashbackData, loadCashbackDisplayConfig])

  const tableRows = useMemo(() => [
    { label: 'ประเภทโบนัส', value: 'คืนยอดเล่นรายวัน' },
    { label: '% โบนัส จากยอดเล่น', value: displayConfig.cashbackRateText || '%' },
    { label: 'โบนัสที่สามารถขอได้', value: 'ตามยอดเสียสุทธิที่เข้าเงื่อนไข' },
    { label: 'ถอนได้สูงสุด', value: 'ขึ้นอยู่กับค่า config ของระบบ' },
    { label: 'เทิร์นกี่เท่าจากยอดเล่น', value: `${latestClaimable?.bonusLock?.turnoverMultiplier || 1}x` },
    { label: 'จำนวนเทิร์นที่จะติด', value: latestClaimable ? 'แสดงตามงวดที่พร้อมรับล่าสุด' : 'รอรายการพร้อมรับ' },
  ], [displayConfig.cashbackRateText, latestClaimable])

  const summaryCards = useMemo(() => ([
    { label: 'รอรับ', value: cashbackSummary.claimableAmount, tone: 'claim' as const },
    { label: 'รออนุมัติ', value: cashbackSummary.pendingAmount, tone: 'pending' as const },
    { label: 'จ่ายแล้ว', value: cashbackSummary.paidAmount, tone: 'paid' as const },
    { label: 'รวมทั้งหมด', value: cashbackSummary.totalEarned, tone: 'total' as const },
  ]), [cashbackSummary.claimableAmount, cashbackSummary.pendingAmount, cashbackSummary.paidAmount, cashbackSummary.totalEarned])

  const formatCurrency = (value?: number) => new Intl.NumberFormat('th-TH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0))

  const formatDate = (value?: string) => {
    if (!value) return '-'
    return new Date(value).toLocaleDateString('th-TH')
  }

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'claimable':
        return 'รอรับ'
      case 'pending':
        return 'รออนุมัติ'
      case 'approved':
        return 'อนุมัติแล้ว'
      case 'paid':
        return 'จ่ายแล้ว'
      case 'rejected':
        return 'ไม่อนุมัติ'
      case 'payment_failed':
        return 'จ่ายไม่สำเร็จ'
      case 'expired':
        return 'หมดอายุ'
      default:
        return status || '-'
    }
  }

  const refreshCashbackData = async () => {
    try {
      setRefreshing(true)
      setClaimMessage('')
      await loadCashbackDisplayConfig()
      await loadCashbackData({ silent: true })
    } catch {
      setRefreshing(false)
      setClaimMessage('รีเฟรชข้อมูล cashback ไม่สำเร็จ')
    }
  }

  const handleClaimCashback = async () => {
    if (!latestClaimable?._id || claiming || cashbackDisabled) return
    if (uiPreviewMode) {
      setClaimMessage('โหมดพรีวิวธีม — ยังไม่ส่งคำขอจริง (ระบบ cashback ปิดอยู่)')
      return
    }
    try {
      setClaiming(true)
      setClaimMessage('')
      await claimCashbackSettlement(latestClaimable._id)
      setClaimMessage('ส่งคำขอรับ cashback สำเร็จแล้ว')
      await refreshCashbackData()
    } catch (error) {
      setClaimMessage(error instanceof Error ? error.message : 'ไม่สามารถรับ cashback ได้')
    } finally {
      setClaiming(false)
    }
  }

  const statusTone = (status?: string) => {
    if (status === 'claimable') return 'claimable'
    if (status === 'pending') return 'pending'
    if (status === 'paid' || status === 'approved') return 'paid'
    return ''
  }

  const canClaim = Boolean(latestClaimable?._id) && !claiming && !cashbackDisabled

  if (loading) {
    return (
      <div className="content htd-cb">
        <div className="htd-cb__glow" aria-hidden />
        <div className="htd-cb__loading">
          <div className="spinner-border" role="status" />
          <p>กำลังโหลดข้อมูล cashback...</p>
        </div>
      </div>
    )
  }

  if (error && !cashbackDisabled) {
    return (
      <div className="content htd-cb">
        <div className="htd-cb__glow" aria-hidden />
        <div className="htd-cb__shell">
          <h3>โหลดข้อมูลไม่สำเร็จ</h3>
          <p>{error}</p>
        </div>
      </div>
    )
  }

  if (cashbackDisabled) {
    return (
      <div className="content htd-cb">
        <div className="htd-cb__glow" aria-hidden />
        <div className="htd-cb__inner">
          <header className="htd-cb__hero htd-cb-anim htd-cb-anim--1">
            <div className="htd-cb__badge" aria-hidden>
              <i className="fas fa-coins" />
            </div>
            <div>
              <p className="htd-cb__eyebrow">{displayConfig.heroBadgeText || 'Cashback Center'}</p>
              <h1 className="htd-cb__title">{displayConfig.pageTitle}</h1>
            </div>
          </header>
          <div className="htd-cb__disabled htd-cb-anim htd-cb-anim--2">
            <span className="htd-cb__disabled-icon" aria-hidden>
              <i className="fas fa-pause-circle" />
            </span>
            <h3>{displayConfig.disabledTitle}</h3>
            <p>{displayConfig.disabledMessage}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="content htd-cb">
      <div className="htd-cb__glow" aria-hidden />
      <div className="htd-cb__inner">
        {uiPreviewMode ? (
          <div className="htd-cb__preview htd-cb-anim htd-cb-anim--1">
            โหมดพรีวิวธีม HTD — ระบบ cashback ปิดอยู่ แสดงข้อมูลตัวอย่างชั่วคราว
          </div>
        ) : null}

        <header className="htd-cb__hero htd-cb-anim htd-cb-anim--1">
          <div className="htd-cb__badge" aria-hidden>
            <i className="fas fa-coins" />
          </div>
          <div>
            <p className="htd-cb__eyebrow">{displayConfig.heroBadgeText || 'Cashback Center'}</p>
            <h1 className="htd-cb__title">{displayConfig.pageTitle}</h1>
            <p className="htd-cb__desc">{displayConfig.pageSubtitle}</p>
          </div>
        </header>

        <section className="htd-cb__claim htd-cb-anim htd-cb-anim--2" aria-label="ยอดพร้อมรับ">
          <div className="htd-cb__claim-copy">
            <h2>{displayConfig.pageTitle}</h2>
            <p>{displayConfig.noticeText}</p>
            <div className="htd-cb__meta">
              <div className="htd-cb__meta-item">
                <span>งวดที่พร้อมรับล่าสุด</span>
                <strong>
                  {latestClaimable
                    ? `${formatDate(latestClaimable.periodStartAt)} - ${formatDate(latestClaimable.periodEndAt)}`
                    : 'ยังไม่มีรายการพร้อมรับ'}
                </strong>
              </div>
              <div className="htd-cb__meta-item">
                <span>Turnover ที่ต้องทำ</span>
                <strong>
                  {latestClaimable ? `${latestClaimable.bonusLock?.turnoverMultiplier || 1}x` : '-'}
                </strong>
              </div>
            </div>
          </div>

          <div className="htd-cb__claim-panel">
            <p className="htd-cb__claim-label">ยอดพร้อมรับตอนนี้</p>
            <p className="htd-cb__claim-amount">
              {latestClaimable ? formatCurrency(latestClaimable.calculation?.cashbackAmount) : '0.00'}
            </p>
            <p className="htd-cb__claim-unit">บาท</p>
            <button
              type="button"
              className="htd-cb__btn"
              onClick={() => void handleClaimCashback()}
              disabled={!canClaim}
              aria-disabled={!canClaim}
            >
              {claiming
                ? 'กำลังดำเนินการ...'
                : latestClaimable?._id
                  ? displayConfig.withdrawalButtonText
                  : 'ยังไม่มีรายการ cashback ให้รับ'}
            </button>
            <button
              type="button"
              className="htd-cb__btn-ghost"
              onClick={() => void refreshCashbackData()}
              disabled={refreshing}
            >
              {refreshing ? 'กำลังรีเฟรช...' : 'รีเฟรชข้อมูล'}
            </button>
            {claimMessage ? <p className="htd-cb__msg">{claimMessage}</p> : null}
          </div>
        </section>

        <section className="htd-cb__stats htd-cb-anim htd-cb-anim--3" aria-label="สรุปยอด">
          {summaryCards.map((card) => (
            <div key={card.label} className={`htd-cb__stat htd-cb__stat--${card.tone}`}>
              <span className="htd-cb__stat-label">{card.label}</span>
              <strong>{formatCurrency(card.value)}</strong>
              <small>บาท</small>
            </div>
          ))}
        </section>

        <div className="htd-cb__grid htd-cb-anim htd-cb-anim--4">
          <section className="htd-cb__panel" aria-label="เงื่อนไขและอัตราเงินคืน">
            <div className="htd-cb__panel-head">
              <div>
                <h3>เงื่อนไขและอัตราเงินคืน</h3>
                <p>แสดงรายละเอียดการรับ cashback ตามการตั้งค่าปัจจุบันของระบบ</p>
              </div>
              <span className="htd-cb__rate-pill">{displayConfig.cashbackRateText}</span>
            </div>

            {displayConfig.showRateTable ? (
              <div className="htd-cb__table-wrap">
                <table className="htd-cb__table">
                  <tbody>
                    {tableRows.map((row) => (
                      <tr key={row.label}>
                        <th scope="row">{row.label}</th>
                        <td>{row.value || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            <div className="htd-cb__note">
              <span>หมายเหตุ</span>
              <p>{displayConfig.noticeText}</p>
            </div>
          </section>

          <section className="htd-cb__panel" aria-label="ภาพรวมการรับ cashback">
            <div className="htd-cb__panel-head">
              <div>
                <h3>ภาพรวมการรับ cashback</h3>
                <p>ตัวอย่างและไฮไลต์สิทธิประโยชน์</p>
              </div>
            </div>

            {displayConfig.showExample ? (
              <div className="htd-cb__example">
                <h4>ตัวอย่างการคำนวณ</h4>
                <p>
                  เล่นบาคาร่าสด
                  <br />
                  แทง Player 1,000,000 บาท ผลออกมาว่า Player ชนะ
                  <br />
                  แทง Player อีกครั้ง 1,000,000 บาท ผลออกมาว่า Banker ชนะ
                  <br />
                  รวมยอดเดิมพันทั้ง 2 ครั้ง คือ 2,000,000 บาท
                  <br />
                  ท่านจะได้รับเงินคืน {displayConfig.cashbackRateText}
                  <br />
                  รายได้ที่จะได้รับขึ้นอยู่กับเรทที่ tenant ตั้งค่าไว้
                  <br />
                  ไม่ว่าท่านจะเล่นแพ้หรือชนะ ก็ได้รับเงินคืนได้
                </p>
              </div>
            ) : null}

            <div className="htd-cb__highlight">
              <span>Highlight</span>
              <p>{displayConfig.promotionalMessage}</p>
            </div>
          </section>
        </div>

        <section className="htd-cb__panel htd-cb-anim htd-cb-anim--5" aria-label="ประวัติ Cashback">
          {cashbackHistory.length > 0 ? (
            <>
              <div className="htd-cb__panel-head">
                <div>
                  <h3>ประวัติ Cashback ล่าสุด</h3>
                  <p>แสดงรายการล่าสุดเพื่อให้ติดตามสถานะการรับ cashback ได้ง่ายขึ้น</p>
                </div>
                <span className="htd-cb__rate-pill">ทั้งหมด {cashbackHistory.length} รายการ</span>
              </div>
              <div className="htd-cb__table-wrap">
                <table className="htd-cb__table">
                  <thead>
                    <tr>
                      <th>ช่วงเวลา</th>
                      <th>ยอดเสียสุทธิ</th>
                      <th>เงินคืน</th>
                      <th>สถานะ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cashbackHistory.map((item) => (
                      <tr key={item._id}>
                        <td>
                          {formatDate(item.periodStartAt)}
                          {' - '}
                          {formatDate(item.periodEndAt)}
                        </td>
                        <td className="htd-cb__loss">{formatCurrency(item.sourceSummary?.netLoss)}</td>
                        <td className="htd-cb__gain">{formatCurrency(item.calculation?.cashbackAmount)}</td>
                        <td>
                          <span
                            className={`htd-cb__status${statusTone(item.status) ? ` htd-cb__status--${statusTone(item.status)}` : ''}`}
                          >
                            {getStatusLabel(item.status)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="htd-cb__empty">
              <p>{displayConfig.historyEmptyText}</p>
            </div>
          )}
        </section>
      </div>
    </div>
  )
}

export default ModernCashbackPage
